import * as React from "react";

import {
  PlpSortingOptions,
  PresentationFacet,
  PresentationFacetPotentialOption,
  PresentationFacet_PresentationType,
  PresentationProduct,
  PresentationSortingOption,
  PresentationTheme,
  PresentationThemeCollection,
  PresentationThemeProduct,
  PresentProductColorInfo,
  ResponseFacet,
  ResponseFacetKey,
  ResponseProduct,
  ResponseProductsSearch,
  ResponseTheme,
  ResponseThemeProduct,
} from "../../model";

import { FacetUrlCache_ObjectEntry } from "../plp-data-target-url-state";
import { useReactPlpInitialState } from "../react-plp-intial-state-provider";

import { useProductListData } from "./product-list-data-provider";
import { useProductListRunningCache } from "./product-list-running-cache-provider";
import { useProductListState } from "./product-list-state-provider";
import { addBannerToProductList, ProductThemes } from "./product-list-utils";

interface ProductListPresentationModelConsumerInterface {
  productCount: number;
  presentationProducts: PresentationProduct[];
  PlpSortingOptions: PresentationSortingOption[];
  presentationThemes: PresentationThemeCollection;
  presentationPageSearchTitle: string;
  hasTheSearchTermBeenRedirected: boolean;
}

interface ProductListPresentationModelConsumer_FacetsInterface {
  presentationFacets: PresentationFacet[];
}

const defaultValue =
  {} as unknown as ProductListPresentationModelConsumerInterface;
const ProductListPresentationModelContext = React.createContext(defaultValue);

const defaultFacetsValue =
  {} as unknown as ProductListPresentationModelConsumer_FacetsInterface;
const ProductListPresentationModelFacetsContext =
  React.createContext(defaultFacetsValue);

export const ProductListPresentationModelProvider: React.FunctionComponent = (
  props
) => {
  const { initialState } = useReactPlpInitialState();
  const { pageConfiguration } = initialState || {};

  const { data } = useProductListData();
  const { selectedOptions } = useProductListState();
  const { facetCache } = useProductListRunningCache();

  const productCount = React.useMemo(() => {
    const lastPageIndex = (data?.pages?.length || 0) - 1;
    if (lastPageIndex < 0) {
      return 0;
    }
    return data?.pages[lastPageIndex]?.results_total || 0;
  }, [data]);

  const transformResponseProductToPresentationProduct = (
    response: ResponseProductsSearch,
    product: ResponseProduct
  ) => {
    let presentationColors = getPresentationColorSwatchesForProduct(product);

    const presentationAutomaticallySelectedColorKey =
      getDefaultSelectedColorForProduct({
        product,
        colorSwatches: presentationColors,
      });

    if (presentationAutomaticallySelectedColorKey) {
      const selectedColorIndex = presentationColors.findIndex(
        (color) => color.key === presentationAutomaticallySelectedColorKey
      );
      if (selectedColorIndex > 0) {
        presentationColors.unshift(
          presentationColors.splice(selectedColorIndex, 1)[0]
        );
      }
    }

    const presentationProduct: PresentationProduct = {
      ...product,
      presentationColors,
      presentationAutomaticallySelectedColorKey,
      fredhopperResponseId: response.response_id,
    };

    return presentationProduct;
  };
  const presentationProducts = React.useMemo(() => {
    const presentationProducts: PresentationProduct[] = (
      data?.pages || []
    ).reduce(
      (acc: PresentationProduct[], page) => [
        ...acc,
        ...page.products.map((product) =>
          transformResponseProductToPresentationProduct(page, product)
        ),
      ],
      []
    );

    const themes = (data?.pages?.[0]?.themes || {}) as unknown as ProductThemes;

    return addBannerToProductList(presentationProducts, themes);
  }, [data]);

  const transformZeroThemeToPresentation = (
    key: string,
    page: ResponseProductsSearch,
    zeroResultsTheme: ResponseTheme
  ): PresentationTheme => {
    if (!zeroResultsTheme) {
      return zeroResultsTheme;
    }
    let presentationProducts = zeroResultsTheme.items.map(
      (product: ResponseThemeProduct): PresentationThemeProduct => {
        let presentationColors =
          getPresentationColorSwatchesForProduct(product);
        const presentationAutomaticallySelectedColorKey =
          getDefaultSelectedColorForProduct({
            product,
            colorSwatches: presentationColors,
          });

        if (presentationAutomaticallySelectedColorKey) {
          const selectedColorIndex = presentationColors.findIndex(
            (color) => color.key === presentationAutomaticallySelectedColorKey
          );
          if (selectedColorIndex > 0) {
            presentationColors.unshift(
              presentationColors.splice(selectedColorIndex, 1)[0]
            );
          }
        }

        const presentationProduct: PresentationThemeProduct = {
          ...product,
          presentationColors,
          presentationAutomaticallySelectedColorKey,
          fredhopperResponseId: page.response_id,
          fredhopperCampaignId: key,
        };

        return presentationProduct;
      }
    );

    return {
      ...zeroResultsTheme,
      items: presentationProducts,
    };
  };
  const presentationThemes = React.useMemo(() => {
    let presentationThemes: PresentationThemeCollection = {};

    const lastPageIndex = (data?.pages?.length || 0) - 1;
    if (lastPageIndex < 0) {
      return presentationThemes;
    }

    const thisPage = data?.pages[lastPageIndex];
    if (!thisPage) {
      return presentationThemes;
    }

    const thisPageThemes = thisPage.themes || {};
    for (const [key, value] of Object.entries(thisPageThemes)) {
      if (!value) {
        continue;
      }

      let presentationTheme: PresentationTheme = {
        ...value,
      };

      if (key === "zero_results" && value) {
        presentationTheme = transformZeroThemeToPresentation(
          key,
          thisPage,
          presentationTheme
        );
      }

      presentationThemes = {
        ...presentationThemes,
        [key]: presentationTheme,
      };
    }

    return presentationThemes;
  }, [data]);

  const transformResponseFacetToPresentationFacet = (
    page: ResponseProductsSearch,
    facet: [string, ResponseFacet]
  ) => {
    const [key, value] = facet;

    const presentationType = determinePresentationTypeOfFacetKey(key);

    let isOpenByDefault = false;
    switch (value.default_state) {
      case "Expanded":
        isOpenByDefault = true;
        break;
      default:
        break;
    }

    const thisPresentationFacet: PresentationFacet = {
      id: value.id,
      fredhopperResponseId: page.response_id,
      key,
      title: value.title,
      hasSubMenu: true,
      hasMultiSelection: value.multi,
      potentialOptions: value.values,
      presentationType,
      isOpenByDefault,
    };

    return thisPresentationFacet;
  };
  const transformThisComponentCachedFacetToPresentationFacet = (
    cachedFacet_ObjectEntry: FacetUrlCache_ObjectEntry
  ) => {
    const [
      facetUrlCacheItem_Key_Name,
      [
        facetUrlCacheItem_DisplayName,
        facetUrlCacheItem_Id,
        facetUrlCacheItem_ResponseId,
      ],
    ] = cachedFacet_ObjectEntry;
    const hasMultiSelection = selectedOptions.some(
      ({ facetName, facetIsMultiSelect }) =>
        facetIsMultiSelect && facetName === facetUrlCacheItem_Key_Name
    );
    const potentialOptions: PresentationFacetPotentialOption[] =
      selectedOptions.reduce(
        (acc: PresentationFacetPotentialOption[], { facetName, id, name }) => {
          if (facetName === facetUrlCacheItem_Key_Name) {
            const thisPresentationFacetPotentialOption: PresentationFacetPotentialOption =
              {
                id,
                count: "?",
                name: name || id,
              };
            acc = [...acc, thisPresentationFacetPotentialOption];
          }
          return acc;
        },
        []
      );

    const presentationType = determinePresentationTypeOfFacetKey(
      facetUrlCacheItem_Key_Name
    );

    const thisPresentationFacet: PresentationFacet = {
      id: facetUrlCacheItem_Id,
      key: facetUrlCacheItem_Key_Name,
      title: facetUrlCacheItem_DisplayName,
      fredhopperResponseId: facetUrlCacheItem_ResponseId,
      hasSubMenu: false,
      hasMultiSelection,
      potentialOptions,
      presentationType,
    };
    return thisPresentationFacet;
  };
  const presentationFacets = React.useMemo(() => {
    const presentationFacetsFromAllThePages: PresentationFacet[] = (
      data?.pages || []
    ).reduce((acc: PresentationFacet[], page) => {
      Object.entries(page.facets).forEach((facet) => {
        const thisPresentationFacet = transformResponseFacetToPresentationFacet(
          page,
          facet
        );

        // if presentation facet with this key already exists overrite it in the acc otherwise add it
        const indexOfAPreexistingAccumulatedPresentationFacetWithTheSameKey =
          acc.findIndex(
            (presentationFacet) =>
              presentationFacet.key === thisPresentationFacet.key
          );
        if (
          indexOfAPreexistingAccumulatedPresentationFacetWithTheSameKey > -1
        ) {
          acc[indexOfAPreexistingAccumulatedPresentationFacetWithTheSameKey] =
            thisPresentationFacet;
        } else {
          acc = [...acc, thisPresentationFacet];
        }
      });
      return acc;
    }, []);

    const presentationFacetsFromTheCache = Object.entries(facetCache).reduce(
      (acc: PresentationFacet[], cachedFacet_ObjectEntry) => {
        const [key] = cachedFacet_ObjectEntry;

        const doesAlreadyExistInPresentationFacets =
          presentationFacetsFromAllThePages.some(
            ({ key: preexistingKey }) => key === preexistingKey
          );

        if (!doesAlreadyExistInPresentationFacets) {
          const thisPresentationFacet =
            transformThisComponentCachedFacetToPresentationFacet(
              cachedFacet_ObjectEntry
            );
          if (thisPresentationFacet) {
            acc = [...acc, thisPresentationFacet];
          }
        }

        return acc;
      },
      []
    );

    return [
      ...presentationFacetsFromAllThePages,
      ...presentationFacetsFromTheCache,
    ];
  }, [data, selectedOptions, facetCache]);

  const presentationPageSearchTitle = React.useMemo(() => {
    let searchTermWasReplacedWith;
    const firstPage = data?.pages?.[0];
    if (firstPage) {
      const thisSearch = firstPage.search;
      const thisMethod = thisSearch?.method;
      const thisReplacements = thisSearch?.replacements || [];
      if (thisMethod === "SPELL CHECK" && thisReplacements?.length === 1) {
        const thisReplacement = thisReplacements[0];
        if (thisReplacement) {
          const thisReplacementValue = thisReplacement.replacement;
          if (thisReplacementValue) {
            searchTermWasReplacedWith = thisReplacementValue;
          }
        }
      }
    }
    if (searchTermWasReplacedWith) {
      return `Showing results for: '${searchTermWasReplacedWith}'`;
    }
    return pageConfiguration.pageTitle || "";
  }, [
    pageConfiguration.pageTitle,
    data?.pages?.[0]?.search?.method,
    data?.pages?.[0]?.search?.replacements,
  ]);

  const hasTheSearchTermBeenRedirected = React.useMemo(() => {
    let searchTermWasReplacedWith;
    const firstPage = data?.pages?.[0];
    if (firstPage) {
      const thisSearch = firstPage.search;
      const thisMethod = thisSearch?.method;
      const thisReplacements = thisSearch?.replacements || [];
      if (thisMethod === "SPELL CHECK" && thisReplacements?.length === 1) {
        const thisReplacement = thisReplacements[0];
        if (thisReplacement) {
          const thisReplacementValue = thisReplacement.replacement;
          if (thisReplacementValue) {
            searchTermWasReplacedWith = thisReplacementValue;
          }
        }
      }
    }

    if (searchTermWasReplacedWith) {
      return true;
    }
    return false;
  }, [
    pageConfiguration.pageTitle,
    data?.pages?.[0]?.search?.method,
    data?.pages?.[0]?.search?.replacements,
  ]);

  const value: ProductListPresentationModelConsumerInterface = React.useMemo(
    () => ({
      productCount,
      presentationProducts,
      PlpSortingOptions,
      presentationThemes,
      presentationPageSearchTitle,
      hasTheSearchTermBeenRedirected,
    }),
    [
      productCount,
      presentationProducts,
      PlpSortingOptions,
      presentationThemes,
      presentationPageSearchTitle,
      hasTheSearchTermBeenRedirected,
    ]
  );
  const facetsValue: ProductListPresentationModelConsumer_FacetsInterface =
    React.useMemo(
      () => ({
        presentationFacets,
      }),
      [presentationFacets]
    );
  return (
    <ProductListPresentationModelContext.Provider value={value}>
      <ProductListPresentationModelFacetsContext.Provider value={facetsValue}>
        {props?.children || []}
      </ProductListPresentationModelFacetsContext.Provider>
    </ProductListPresentationModelContext.Provider>
  );
};

export const useProductListPresentationModel = () => {
  const value = React.useContext(ProductListPresentationModelContext);
  if (value === defaultValue) {
    throw new Error(
      "useProductListPresentationModel must be used within a ProductListPresentationModelProvider"
    );
  }
  return value;
};

export const useProductListPresentationModelFacets = () => {
  const value = React.useContext(ProductListPresentationModelFacetsContext);
  if (value === defaultFacetsValue) {
    throw new Error(
      "useProductListPresentationModelFacets must be used within a ProductListPresentationModelProvider"
    );
  }
  return value;
};

const getPresentationColorSwatchesForProduct = (
  product?: ResponseProduct | ResponseThemeProduct
): PresentProductColorInfo[] => {
  let colorSwatches: PresentProductColorInfo[] = [];

  if (product && product.bbnt_color_swatch) {
    for (const [key, colorSwatch] of Object.entries(
      product.bbnt_color_swatch
    )) {
      switch (colorSwatch.type) {
        case "1":
          // hex value color swatch
          colorSwatches = [
            ...colorSwatches,
            ...[
              {
                key,
                value: colorSwatch.value,
                isImage: false,
              },
            ],
          ];
          break;
        case "2":
          // image color swatch
          colorSwatches = [
            ...colorSwatches,
            ...[
              {
                key,
                value: colorSwatch.value,
                isImage: true,
              },
            ],
          ];
          break;
        default:
          // some other unsupported thing
          break;
      }
    }

    // @ts-ignore
    if (product.default_group_id) {
      const productId = product.bbnt_color_info?.find(
        (info) => info.products?.[0] === product.default_group_id
      )?.id;
      const defSwatch = productId
        ? product.bbnt_color_swatch[productId]?.value
        : null;
      const defSwatchObj = defSwatch
        ? colorSwatches.find(
            (swatch) => swatch.value.toUpperCase() === defSwatch.toUpperCase()
          )
        : null;

      if (defSwatchObj) {
        const index = colorSwatches.indexOf(defSwatchObj);

        if (index > -1) {
          colorSwatches.splice(index, 1);
          colorSwatches.unshift(defSwatchObj);
        }
      }
    }
  }

  return colorSwatches;
};

const getDefaultSelectedColorForProduct = ({
  product,
  colorSwatches,
}: {
  product?: ResponseProduct | ResponseThemeProduct;
  colorSwatches?: PresentProductColorInfo[];
}): string | undefined => {
  colorSwatches = colorSwatches
    ? colorSwatches
    : getPresentationColorSwatchesForProduct(product);

  let productColorInfo;
  const productMagentoId = product?.magento_id_product;
  if (product && productMagentoId && product.bbnt_color_info) {
    const matchingColorInfo = product.bbnt_color_info.find(({ products }) =>
      products.some((productId) => productId === productMagentoId)
    );
    if (matchingColorInfo && !!matchingColorInfo.label) {
      productColorInfo = matchingColorInfo;
    } else {
      let firstColorBaseEntryValue: string | undefined;
      const productColorBaseEntries = Object.entries(product.colour_base || {});
      if (productColorBaseEntries.length > 0) {
        const firstEntry = productColorBaseEntries[0];
        if (firstEntry.length > 1) {
          firstColorBaseEntryValue = firstEntry[1];
        }
      }

      if (firstColorBaseEntryValue) {
        productColorInfo = product.bbnt_color_info.find(
          ({ label }) => label === firstColorBaseEntryValue
        );
      }
    }
  }

  const automaticallySelectedColorKey =
    productColorInfo?.id ||
    (colorSwatches.length > 0 ? colorSwatches[0].key : undefined);

  return automaticallySelectedColorKey;
};

const determinePresentationTypeOfFacetKey = (facetKey: string) => {
  let presentationType: PresentationFacet_PresentationType =
    PresentationFacet_PresentationType.unspecified;
  switch (facetKey) {
    case ResponseFacetKey.colour_base_family:
      presentationType = PresentationFacet_PresentationType.colors;
      break;
    default:
      presentationType = PresentationFacet_PresentationType.unspecified;
      break;
  }
  return presentationType;
};
